import { useCallback, useRef } from 'react';

interface LetterEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Plain-text editor that preserves [CITE:xxx] markers.
 * Uses a textarea to avoid losing cite markers that rich editors might strip.
 * The citation panel renders the visual representation separately.
 */
export function LetterEditor({ value, onChange, disabled, className = '' }: LetterEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  return (
    <div className={`relative ${className}`}>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        aria-label="Appeal letter content"
        spellCheck
        className="h-full w-full resize-none rounded-lg border border-gray-200 bg-white p-4 font-mono text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
        placeholder="Appeal letter will appear here once generation is complete..."
      />
      <div className="pointer-events-none absolute bottom-2 right-3 text-[11px] text-gray-400">
        {value.length.toLocaleString()} chars
      </div>
    </div>
  );
}
